// "use client"

// import { useState } from "react"
// import { motion } from "framer-motion"
// import { Code, Wand2, Layers, Sparkles, Cpu, Braces } from "lucide-react"

// const features = [
//   {
//     title: "Code Mode",
//     description: "Full control with direct code editing for developers who want precision",
//     icon: Code,
//     color: "from-blue-500 to-cyan-400",
//     snippet: "const Button = () => <button className='btn'>Click me</button>",
//   },
//   {
//     title: "No-Code Mode",
//     description: "Drag & drop interface for visual creators with zero coding required",
//     icon: Wand2,
//     color: "from-purple-500 to-pink-400",
//     snippet: "// No code needed! Just drag and drop components",
//   },
//   {
//     title: "Low-Code Mode",
//     description: "The perfect middle ground with visual tools and light coding",
//     icon: Layers,
//     color: "from-amber-500 to-orange-400",
//     snippet: "// Modify only what you need\nbutton.onClick = () => alert('Hello!')",
//   },
//   {
//     title: "AI Assistant",
//     description: "Ask for changes in plain English and watch them happen instantly",
//     icon: Sparkles,
//     color: "from-emerald-500 to-green-400",
//     snippet: '// "Make the button blue and add a hover effect"',
//   },
//   {
//     title: "Real-time Collaboration",
//     description: "Work together with your team in perfect sync across all modes",
//     icon: Cpu,
//     color: "from-red-500 to-pink-400",
//     snippet: "// Alice and Bob are editing this section...",
//   },
//   {
//     title: "Component Library",
//     description: "Thousands of ready-to-use components to accelerate development",
//     icon: Braces,
//     color: "from-indigo-500 to-purple-400",
//     snippet: "import { Carousel, Modal } from '@raffaello/ui'",
//   },
// ]

// export function MissionGridSection() {
//   const [activeCard, setActiveCard] = useState<number | null>(null)

//   return (
//     <section className="py-24 px-4 relative overflow-hidden " id="why-raffaello">
//       <div className="absolute inset-0 "></div>
//       <div className="container mx-auto relative z-10">
//         <div className="text-center mb-16 backdrop-blur-sm" >
//           <h2 className="text-3xl md:text-5xl font-bold mb-6">Why Raffaello?</h2>
//           <p className="text-xl text-card-foreground/30 max-w-3xl mx-auto">
//             A revolutionary approach to web development that adapts to your workflow
//           </p>
//         </div>

//         <motion.div
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 0.5 }}
//           className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
//         >
//           {features.map((feature, index) => (
//             <motion.div
//               key={index}
//               initial={{ y: 50, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{
//                 duration: 0.5,
//                 delay: index * 0.1,
//                 type: "spring",
//                 stiffness: 100,
//               }}
//               whileHover={{
//                 scale: 1.02,
//                 transition: { duration: 0.2 },
//               }}
//               className="relative group"
//               onMouseEnter={() => setActiveCard(index)}
//               onMouseLeave={() => setActiveCard(null)}
//             >
//               <div
//                 className="absolute inset-0 bg-gradient-to-r opacity-20 rounded-xl blur-xl transition-opacity duration-300 group-hover:opacity-40"
//                 style={{
//                   backgroundImage: `linear-gradient(to right, ${feature.color.split(" ")[1]}, ${feature.color.split(" ")[3]})`,
//                 }}
//               ></div>

//               <div className="h-full bg-black/60 backdrop-blur-sm border border-white/10 rounded-xl p-6 relative overflow-hidden transition-all duration-300 group-hover:border-purple-500/50 group-hover:shadow-lg group-hover:shadow-purple-500/10">
//                 <div
//                   className={`absolute top-0 right-0 h-1 w-0 bg-gradient-to-r ${feature.color} transition-all duration-700 group-hover:w-full`}
//                 ></div>

//                 <div className="flex items-center mb-4">
//                   <div className={`p-2 rounded-lg bg-gradient-to-r ${feature.color} mr-3`}>
//                     <feature.icon className="h-5 w-5 text-white" />
//                   </div>
//                   <h3 className="text-xl font-bold">{feature.title}</h3>
//                 </div>

//                 <p className="text-card-foreground/30 mb-6">{feature.description}</p>

//                 <motion.div
//                   initial={{ opacity: 0, y: 20 }}
//                   animate={{
//                     opacity: activeCard === index ? 1 : 0.7,
//                     y: activeCard === index ? 0 : 10,
//                   }}
//                   transition={{ duration: 0.3 }}
//                   className="bg-gray-900/80 border border-gray-800 rounded-lg p-3 font-mono text-sm text-card-foreground/30 overflow-hidden"
//                 >
//                   <pre className="whitespace-pre-wrap break-words">
//                     <code>{feature.snippet}</code>
//                   </pre>
//                 </motion.div>
//               </div>
//             </motion.div>
//           ))}
//         </motion.div>
//       </div>
//     </section>
//   )
// }

"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import {
  Code,
  Wand2,
  Layers,
  Sparkles,
  Cpu,
  Braces,
  Globe,
} from "lucide-react"

const features = [
  {
    title: "Code, Visual & AI Editing",
    description: "Switch freely between coding, dragging, or prompting with AI — all in the same interface.",
    icon: Code,
    color: "from-blue-500 to-cyan-400",
    snippet: `// Any way you like:\n<button className='btn'>Click</button>\n// Or say: \"Make the button green with rounded corners\"`,
  },
  {
    title: "Zero Lock-In",
    description: "Open standard code output. No proprietary stack. No framework lock-in.",
    icon: Globe,
    color: "from-gray-500 to-neutral-400",
    snippet: `// Framework-free output\nindex.html, style.css, app.js`,
  },
  {
    title: "Fast Content Publishing",
    description: "Update your site in minutes — blog posts, landing pages, services — from any device.",
    icon: Wand2,
    color: "from-purple-500 to-pink-400",
    snippet: `// Written on mobile\n\"Add new blog post: '5 Tips for Freelancers'\"`,
  },
  {
    title: "AI with Contextual Awareness",
    description: "AI that only edits what you ask, without breaking unrelated parts of your site.",
    icon: Sparkles,
    color: "from-emerald-500 to-green-400",
    snippet: `// Scoped edit:\n\"Change this selected button text to 'Buy Now'\"`,
  },
  {
    title: "Version Control & Collaboration",
    description: "Full Git support with branching, PRs, and team editing in real-time.",
    icon: Cpu,
    color: "from-red-500 to-pink-400",
    snippet: `// Edit together\nfeature/newsletter-signup → main`,
  },
  {
    title: "Component System & UI Kits",
    description: "Create reusable elements and convert them to any framework.",
    icon: Braces,
    color: "from-indigo-500 to-purple-400",
    snippet: `//Convert to any framework \nConvert this element to a NextJS component.`,
  },
]

export function MissionGridSection() {
  const [activeCard, setActiveCard] = useState<number | null>(null)

  return (
    <section className="py-24 px-4 relative overflow-hidden " id="why-raffaello">
      <div className="absolute inset-0 "></div>
      <div className="container mx-auto relative z-10">
        <div className="text-center mb-16 backdrop-blur-sm" >
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Why Raffaello?</h2>
          <p className="text-xl text-card-foreground max-w-3xl mx-auto">
            A truly modern web editor — built for creators, developers, and teams who want control without compromise.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                type: "spring",
                stiffness: 100,
              }}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.2 },
              }}
              className="relative group"
              onMouseEnter={() => setActiveCard(index)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div
                className="absolute inset-0 bg-gradient-to-r opacity-20 rounded-xl blur-xl transition-opacity duration-300 group-hover:opacity-40"
                style={{
                  backgroundImage: `linear-gradient(to right, ${feature.color.split(" ")[1]}, ${feature.color.split(" ")[3]})`,
                }}
              ></div>

              <div className="h-full bg-black/60 backdrop-blur-sm border border-white/10 rounded-xl p-6 relative overflow-hidden transition-all duration-300 group-hover:border-purple-500/50 group-hover:shadow-lg group-hover:shadow-purple-500/10">
                <div
                  className={`absolute top-0 right-0 h-1 w-0 bg-gradient-to-r ${feature.color} transition-all duration-700 group-hover:w-full`}
                ></div>

                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${feature.color} mr-3`}>
                    <feature.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">{feature.title}</h3>
                </div>

                <p className="text-card-foreground/30 mb-6">{feature.description}</p>

                {/* <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: activeCard === index ? 1 : 0.7,
                    y: activeCard === index ? 0 : 10,
                  }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-900/80 border border-gray-800 rounded-lg p-3 font-mono text-sm text-card-foreground/30 overflow-hidden"
                >
                  <pre className="whitespace-pre-wrap break-words">
                    <code>{feature.snippet}</code>
                  </pre>
                </motion.div> */}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
